import React, { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { grantScope } from "./accessManagerApi";
import ScopeTree from "./ScopeTree";
import "./AccessManagerPanel.css";

/**
 * AddUserDialog — modal dialog for granting initial scopes to a new user.
 *
 * Provides an email input with HTML5 validation, a ScopeTree for selecting
 * scopes, and a required notes field. Calls grantScope for each selected
 * scope on submit.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {() => void} props.onClose - Called when the dialog should close.
 * @param {(email: string, scopes: string[], notes: string) => Promise<void>} props.onAdd - Called on successful add.
 */
export default function AddUserDialog({ open, onClose, onAdd }) {
  const [email, setEmail] = useState("");
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setEmail("");
      setSelectedScopes([]);
      setNotes("");
      setError(null);
    }
  }, [open]);

  const isValidEmail = (value) => {
    // HTML5 email validation pattern
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleSubmit = async () => {
    setError(null);

    // Validate email
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    if (!isValidEmail(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }

    // Validate scopes
    if (selectedScopes.length === 0) {
      setError("At least one scope must be selected.");
      return;
    }

    // Validate notes
    if (!notes.trim()) {
      setError("Notes are required.");
      return;
    }

    setLoading(true);

    try {
      // Call grantScope for each selected scope
      for (const scope of selectedScopes) {
        await grantScope(email.trim(), scope, notes.trim());
      }

      if (onAdd) {
        await onAdd(email.trim(), selectedScopes, notes.trim());
      }
      onClose();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to grant access.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="access-manager-dialog__overlay" />
        <Dialog.Content className="access-manager-dialog__content">
          <Dialog.Title className="access-manager-dialog__title">
            Add User
          </Dialog.Title>
          <Dialog.Description className="access-manager-dialog__description">
            Grant access scopes to a new user.
          </Dialog.Description>

          <div className="access-manager-dialog__field">
            <label
              htmlFor="add-user-email"
              className="access-manager-dialog__notes-label"
            >
              Email <span aria-hidden="true">*</span>
            </label>
            <input
              id="add-user-email"
              type="email"
              className="access-manager-dialog__email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
              disabled={loading}
              autoComplete="email"
            />
          </div>

          <ScopeTree
            selectedScopes={selectedScopes}
            onChange={setSelectedScopes}
            disabled={loading}
          />

          <div className="access-manager-dialog__notes">
            <label
              htmlFor="add-user-notes"
              className="access-manager-dialog__notes-label"
            >
              Notes <span aria-hidden="true">*</span>
            </label>
            <textarea
              id="add-user-notes"
              className="access-manager-dialog__notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for granting access (required)"
              required
              disabled={loading}
              rows={3}
            />
          </div>

          {error && (
            <div className="access-manager-panel__error" role="alert">
              {error}
            </div>
          )}

          <div className="access-manager-dialog__actions">
            <Dialog.Close asChild>
              <button
                type="button"
                className="access-manager-dialog__btn access-manager-dialog__btn--cancel"
                disabled={loading}
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              className="access-manager-dialog__btn access-manager-dialog__btn--confirm"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Adding…" : "Add User"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
